import { Platform } from 'react-native';
import DOMPurify from 'isomorphic-dompurify';
import { guideCardToPlainText } from './guide_card_text';

// Tags and attributes we keep when serializing rendered message DOM for the
// clipboard's text/html payload. Anything not on these lists is stripped so we
// never leak Gab AI's internal styles, classNames, refs, or runtime markup
// into paste targets like Word, Google Docs, or Obsidian.
const ALLOWED_TAGS = [
  'p', 'div', 'span', 'br',
  's', 'strong', 'b', 'i', 'em', 'u',
  'code', 'pre',
  'ol', 'ul', 'li',
  'a',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'tr', 'td', 'th', 'tbody', 'thead', 'tfoot',
  'blockquote',
  'sup', 'sub',
];

// `class` is kept so the `language-xxx` hint we attach to copied <code>
// blocks survives sanitization — many markdown editors (Obsidian, etc.)
// use it to pick syntax highlighting on the destination side.
const ALLOWED_ATTR = ['href', 'colspan', 'rowspan', 'class'];

const FORBID_TAGS = ['style', 'script'];
const FORBID_ATTR = ['style', 'id', 'tabindex', 'role', 'aria-label'];

const TABLE_CELL_STYLE = 'border:1px solid #ccc;padding:8px;text-align:left;vertical-align:top';
const TABLE_STYLE = 'border-collapse:collapse;width:100%';

const isTableDivider = (line) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const parseTableRow = (line) => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => cell.trim());

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * Parse markdown pipe tables into row arrays. Used at copy time so paste
 * targets receive real <table> markup even though the live UI renders tables
 * as flexbox Views for scrolling/styling.
 *
 * @param {string} markdown
 * @returns {string[][][]}
 */
export const parseMarkdownTables = (markdown) => {
  const raw = typeof markdown === 'string' ? markdown : String(markdown || '');
  if (!raw.trim()) return [];

  const lines = raw.replace(/\r\n?/g, '\n').split('\n');
  const tables = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!/^\s*\|.*\|\s*$/.test(line)) continue;
    if (i + 1 >= lines.length || !isTableDivider(lines[i + 1])) continue;

    const rows = [parseTableRow(line)];
    i += 2;
    while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
      rows.push(parseTableRow(lines[i]));
      i++;
    }
    if (rows.length > 0) {
      tables.push(rows);
    }
  }

  return tables;
};

/**
 * @param {string[][]} rows
 * @returns {string}
 */
export const rowsToHtmlTable = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return '';

  const [headerRow, ...bodyRows] = rows;
  const headerCells = (headerRow || []).map(
    (cell) => `<th style="${TABLE_CELL_STYLE}">${escapeHtml(cell)}</th>`
  ).join('');
  const bodyHtml = bodyRows.map((row) => {
    const cells = row.map(
      (cell) => `<td style="${TABLE_CELL_STYLE}">${escapeHtml(cell)}</td>`
    ).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  return `<table style="${TABLE_STYLE}"><thead><tr>${headerCells}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
};

const injectMarkdownTables = (root, markdown) => {
  const tables = parseMarkdownTables(markdown);
  if (tables.length === 0) return;

  const placeholders = root.querySelectorAll('[data-message-table]');
  placeholders.forEach((el, index) => {
    const html = rowsToHtmlTable(tables[index]);
    if (!html) return;

    const replacement = document.createElement('div');
    replacement.innerHTML = html;
    const tableEl = replacement.firstElementChild;
    if (tableEl) {
      el.replaceWith(tableEl);
    }
  });
};

const FENCE_OPEN = /^(\s{0,3})(`{3,}|~{3,})\s*([^\s`~]*)\s*$/;

/**
 * Parse fenced code blocks (``` or ~~~) from a markdown source in document
 * order, returning the raw text content and language hint for each. Used at
 * copy time so paste targets receive a proper `<pre><code>` (preserving
 * newlines and avoiding the in-app header chrome) instead of the live UI's
 * ScrollView + Text widget, which serializes as a single `<div>` and loses
 * line breaks when rich-text editors convert HTML back to markdown.
 *
 * @param {string} markdown
 * @returns {{ language: string, content: string }[]}
 */
export const parseMarkdownCodeBlocks = (markdown) => {
  const raw = typeof markdown === 'string' ? markdown : String(markdown || '');
  if (!raw) return [];

  const lines = raw.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];

  for (let i = 0; i < lines.length; i++) {
    const openMatch = lines[i].match(FENCE_OPEN);
    if (!openMatch) continue;

    const fence = openMatch[2];
    const language = (openMatch[3] || '').trim();
    const body = [];
    let j = i + 1;

    while (j < lines.length) {
      const closeRe = new RegExp(`^\\s{0,3}${fence[0]}{${fence.length},}\\s*$`);
      if (closeRe.test(lines[j])) break;
      body.push(lines[j]);
      j++;
    }

    blocks.push({ language, content: body.join('\n') });
    // Skip past the closing fence (or to end of input if it was never closed)
    i = j;
  }

  return blocks;
};

/**
 * @param {{ language: string, content: string }} block
 * @returns {string}
 */
export const codeBlockToHtml = ({ language, content }) => {
  const langAttr = language && /^[A-Za-z0-9_+\-.#]+$/.test(language)
    ? ` class="language-${language}"`
    : '';
  return `<pre><code${langAttr}>${escapeHtml(content)}</code></pre>`;
};

const injectMarkdownCodeBlocks = (root, markdown) => {
  const placeholders = root.querySelectorAll('[data-message-code]');
  if (placeholders.length === 0) return;

  const guideKinds = new Set(Array.from(root.querySelectorAll('[data-guide-kind]'), el => el.getAttribute('data-guide-kind')));
  const blocks = parseMarkdownCodeBlocks(markdown).filter(block => (
    !guideKinds.has(block.language.trim().toLowerCase()) || guideCardToPlainText(block.language, block.content) === null
  ));

  placeholders.forEach((el, index) => {
    // Prefer the parsed source-of-truth from markdown, but fall back to the
    // placeholder's own textContent (with header chrome stripped) so we never
    // emit an empty <pre><code> if parsing somehow misses a block.
    const block = blocks[index] || {
      language: el.getAttribute('data-code-language') || '',
      content: (() => {
        const clone = el.cloneNode(true);
        clone.querySelectorAll('[data-copy-exclude]').forEach((n) => n.remove());
        return clone.textContent || '';
      })(),
    };
    const replacement = document.createElement('div');
    replacement.innerHTML = codeBlockToHtml(block);
    const codeEl = replacement.firstElementChild;
    if (codeEl) {
      el.replaceWith(codeEl);
    }
  });
};

/**
 * Extract a sanitized HTML representation of a rendered assistant message,
 * suitable for use as the `text/html` payload of a ClipboardItem. Returns
 * `undefined` on native or when the message element cannot be located so
 * callers can fall back to a plain-text-only copy.
 *
 * @param {string} messageId
 * @param {string} [markdown] - Raw message markdown; when provided, table
 *   widgets in the DOM are replaced with semantic HTML tables and code-block
 *   widgets are replaced with `<pre><code>` (preserving newlines) for paste
 *   targets like Word, Google Docs, and Obsidian.
 * @returns {string | undefined}
 */
const getMessageHtmlForCopy = (messageId, markdown) => {
  if (Platform.OS !== 'web' || !messageId) return undefined;
  if (typeof document === 'undefined') return undefined;

  // MessageContent is a React Native View — on web that renders as a div with
  // aria-label from accessibilityLabel, not an article element.
  const messageEl = document.querySelector(
    `div.message-bubble-wrapper[data-message-id="${messageId}"] [aria-label="message-content"]`
  );
  if (!messageEl) return undefined;

  try {
    const clone = messageEl.cloneNode(true);

    // Code blocks must run BEFORE the generic copy-exclude pass so we can
    // see the header chrome inside each placeholder and use it as a fallback
    // source when parsing the markdown source fails.
    if (markdown) {
      injectMarkdownTables(clone, markdown);
      injectMarkdownCodeBlocks(clone, markdown);
    } else {
      // Even without source markdown, fall back to DOM-only code-block
      // reconstruction so newlines survive.
      injectMarkdownCodeBlocks(clone, '');
    }

    clone.querySelectorAll('[data-copy-exclude]').forEach((el) => el.remove());

    return DOMPurify.sanitize(clone.innerHTML, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS,
      FORBID_ATTR,
    });
  } catch (err) {
    console.warn('Failed to sanitize message HTML for copy:', err);
    return undefined;
  }
};

export default getMessageHtmlForCopy;
